import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  signal,
} from '@angular/core'
import { ActivatedRoute, Router } from '@angular/router'

import { TagModule } from 'primeng/tag'

import { Subscription } from 'rxjs'
import { tap } from 'rxjs/operators'

import { FlxTabBarModule, FlxTabComponent, FlxTemplateDirective } from '@flowx/angular-ui-toolkit'
import { PanelSize } from '@flowx/designer/modules/process-details/process-details-helper.service'
import { InstanceAuditLogsComponent } from '@flowx/designer/modules/process/components/instance-audit-logs/instance-audit-logs.component'
import { InstanceDocumentsComponent } from '@flowx/designer/modules/process/components/instance-documents/instance-documents.component'
import { InstanceExceptionsComponent } from '@flowx/designer/modules/process/components/instance-exceptions/instance-exceptions.component'
import { InstanceNotificationsComponent } from '@flowx/designer/modules/process/components/instance-notifications/instance-notifications.component'
import { InstanceTriggerComponent } from '@flowx/designer/modules/process/components/instance-trigger/instance-trigger.component'
import { ProcessInstanceDetailsPanelHeaderComponent } from '@flowx/designer/modules/process/components/process-instance-details-panel-header/process-instance-details-panel-header.component'
import { ProcessInstanceSubprocessesComponent } from '@flowx/designer/modules/process/components/process-instance-subprocesses/process-instance-subprocesses.component'
import { ProcessInstanceTokensComponent } from '@flowx/designer/modules/process/components/process-instance-tokens/process-instance-tokens.component'
import { ProcessInstanceVariablesComponent } from '@flowx/designer/modules/process/components/process-instance-variables/process-instance-variables.component'
import { ExpandablePanelComponent } from '@flowx/designer/shared/components/bottom-panel/expandable-panel/expandable-panel.component'
import { InstanceWorkflowsComponent } from '@flowx/designer/shared/components/instance-workflows/instance-workflows.component'
import { ProcessData } from '@flowx/designer/shared/entities/process-instance.types'

export enum TabHeaders {
  VARIABLES = 'Variables',
  TOKENS = 'Tokens',
  SUBPROCESSES = 'Subprocesses',
  EXCEPTIONS = 'Exceptions',
  WORKFLOWS = 'Workflows',
  TRIGGER = 'Trigger',
  NOTIFICATIONS = 'Notifications',
  AUDIT = 'Audit Log',
  DOCUMENTS = 'Documents',
}

export const ProcessInstanceDetailsTabs = [
  { id: 'variables', header: TabHeaders.VARIABLES },
  { id: 'tokens', header: TabHeaders.TOKENS },
  { id: 'subprocesses', header: TabHeaders.SUBPROCESSES },
  { id: 'audit', header: TabHeaders.AUDIT },
  { id: 'workflows', header: TabHeaders.WORKFLOWS },
  { id: 'trigger', header: TabHeaders.TRIGGER },
  { id: 'notifications', header: TabHeaders.NOTIFICATIONS },
  { id: 'documents', header: TabHeaders.DOCUMENTS },
  { id: 'exceptions', header: TabHeaders.EXCEPTIONS },
]

@Component({
  selector: 'app-process-instance-details-panel',
  templateUrl: './process-instance-details-panel.component.html',
  styleUrls: ['./process-instance-details-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    ProcessInstanceDetailsPanelHeaderComponent,
    ExpandablePanelComponent,
    FlxTabBarModule,
    TagModule,
    ProcessInstanceVariablesComponent,
    ProcessInstanceTokensComponent,
    InstanceAuditLogsComponent,
    InstanceWorkflowsComponent,
    FlxTemplateDirective,
    InstanceDocumentsComponent,
    InstanceExceptionsComponent,
    InstanceNotificationsComponent,
    InstanceTriggerComponent,
    ProcessInstanceSubprocessesComponent,
  ],
})
export class ProcessInstanceDetailsPanelComponent implements OnInit, OnDestroy {
  #route = inject(ActivatedRoute)
  #router = inject(Router)
  private subscriptions = new Subscription()

  @Input() process: ProcessData
  @Output() subprocessNavigation = new EventEmitter<string>()

  @HostBinding('class')
  get class(): PanelSize {
    return this.size()
  }

  selectedTabIndex = 0
  tabNames = TabHeaders
  tabs = []
  panelSize = PanelSize
  size = signal<PanelSize>(PanelSize.MEDIUM)

  ngOnInit(): void {
    this.tabs = ProcessInstanceDetailsTabs.filter((tab) => {
      if (tab.header === TabHeaders.EXCEPTIONS) {
        return this.process?.activeIncidents > 0
      }
      if (tab.header === TabHeaders.TRIGGER) {
        return !!this.process?.initiator
      }
      return true
    })
    const queryParamMap$ = this.#route.queryParamMap.pipe(
      tap((queryParamMap) => {
        this.selectedTabIndex = this.getTabIndex(queryParamMap.get('tab'))
      })
    )
    this.subscriptions.add(queryParamMap$.subscribe())
  }

  onGoToProcess(event: string): void {
    this.subprocessNavigation.emit(event)
  }

  onTabChange(tab: FlxTabComponent): void {
    const tabQueryParam = this.tabs.find((item) => item.header === tab.label()).id
    this.#router.navigate([], {
      relativeTo: this.#route,
      queryParams: {
        tab: tabQueryParam,
      },
      queryParamsHandling: 'merge',
    })
  }

  getTabIndex(tabId: string): number {
    if (!tabId) {
      return 0
    }
    const index = this.tabs.findIndex((x) => x.id === tabId)
    return index >= 0 ? index : 0
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe()
  }
}
